import { PracticeIcon } from '../design/PracticeIcon';
import { Icon } from '../design/Icon';
import { difficulties, themes, themeLabel } from '../domain/puzzle/catalog';
import type { Settings } from '../features/settings/settings';
import { customRangeError } from '../features/settings/settings';
import { useId } from 'react';

const shortLabels: Record<string, string> = {
  discoveredAttack: 'Discovered',
  backRankMate: 'Back rank',
  underPromotion: 'Under-promo',
  defensiveMove: 'Defensive',
  hangingPiece: 'Hanging',
  trappedPiece: 'Trapped',
};
export function PracticeSetup({
  draft,
  update,
  step,
  setStep,
  start,
  review,
}: {
  /** R5: missed puzzles due now and coming up later. */
  review?: { due: number; upcoming: number };
  draft: Settings;
  update: (patch: Partial<Settings>) => void;
  step: 'difficulty' | 'theme';
  setStep: (step: 'difficulty' | 'theme') => void;
  start: () => void;
}) {
  const reviewing = draft.mode === 'review';
  const rangeError =
    !reviewing && draft.difficulty === 'custom' ? customRangeError(draft) : undefined;
  const rangeErrorId = useId();
  const reviewTotal = (review?.due ?? 0) + (review?.upcoming ?? 0);
  return (
    <div
      className={`practice-setup settings-grid ${step === 'theme' ? 'theme-setup' : ''} ${draft.difficulty === 'custom' ? 'custom-setup' : ''}`}
    >
      {step === 'difficulty' ? (
        <>
          <div className="practice-modes" role="group" aria-label="Practice choices">
            {(['mixed', 'theme', 'review'] as const).map((mode) => (
              <button
                key={mode}
                aria-pressed={draft.mode === mode}
                onClick={() => update({ mode })}
              >
                {mode === 'mixed' ? 'Endless mix' : mode === 'theme' ? 'Theme practice' : 'Review'}
                {mode === 'review' && !!review?.due && (
                  <span className="mode-badge" data-testid="review-due-badge">
                    {review.due}
                  </span>
                )}
              </button>
            ))}
          </div>
          <p className="mode-description">
            {draft.mode === 'mixed'
              ? 'A fresh mix of tactical ideas.'
              : reviewing
                ? 'Puzzles you missed come back after a few days.'
                : 'Focus on one skill at a time.'}
          </p>
          {reviewing ? (
            <section className="review-summary" data-testid="review-summary" aria-live="polite">
              <p className="review-count">
                {review?.due ? (
                  <>
                    <strong>{review.due}</strong> due now
                  </>
                ) : reviewTotal ? (
                  'Nothing due yet'
                ) : (
                  'Nothing to review yet'
                )}
              </p>
              <p className="muted">
                {review?.upcoming
                  ? `${review.upcoming} more coming up${review.due ? ' later' : '. You can review them early'}.`
                  : reviewTotal
                    ? 'That’s everything you have missed.'
                    : 'Missed puzzles show up here a day or two later.'}
              </p>
              <p className="muted review-rule">
                Solve one cleanly and it comes back in a week, then in three weeks, then it’s
                cleared.
              </p>
            </section>
          ) : (
            <fieldset className="choice-field difficulty-field">
              <legend>Difficulty</legend>
              <button
                className="adaptive-choice"
                aria-pressed={draft.difficulty === 'adaptive'}
                onClick={() => update({ difficulty: 'adaptive' })}
              >
                <PracticeIcon name="adaptive" difficulty />
                <strong>Adaptive</strong>
                <small>Adjusts to how you're doing</small>
              </button>
              <div className="icon-choices difficulty-choices">
                {Object.entries(difficulties).map(([name, range]) => (
                  <button
                    key={name}
                    aria-pressed={draft.difficulty === name}
                    onClick={() => update({ difficulty: name as Settings['difficulty'] })}
                  >
                    <PracticeIcon name={name} difficulty />
                    <strong>{themeLabel(name)}</strong>
                    <small>{name === 'custom' ? 'Your range' : range.join('–')}</small>
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          {!reviewing && draft.difficulty === 'custom' && (
            <div className="rating-range">
              <label>
                Minimum rating
                <input
                  type="number"
                  min="0"
                  max="4000"
                  value={Number.isFinite(draft.ratingMin) ? draft.ratingMin : ''}
                  aria-invalid={!!rangeError}
                  aria-describedby={rangeError ? rangeErrorId : undefined}
                  onChange={(e) => update({ ratingMin: e.target.valueAsNumber })}
                />
              </label>
              <label>
                Maximum rating
                <input
                  type="number"
                  min="0"
                  max="4000"
                  value={Number.isFinite(draft.ratingMax) ? draft.ratingMax : ''}
                  aria-invalid={!!rangeError}
                  aria-describedby={rangeError ? rangeErrorId : undefined}
                  onChange={(e) => update({ ratingMax: e.target.valueAsNumber })}
                />
              </label>
            </div>
          )}
          {!reviewing && (
            <p
              className={`rating-note${rangeError ? ' danger' : ''}`}
              id={rangeErrorId}
              role={rangeError ? 'status' : undefined}
            >
              {rangeError ?? 'Puzzle difficulty. No player rating to chase.'}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="theme-step-heading">
            <button aria-label="Back to difficulty" onClick={() => setStep('difficulty')}>
              ‹ Difficulty: {themeLabel(draft.difficulty)}
            </button>
            <h1>Choose a theme</h1>
          </div>
          <div className="theme-scroll" role="group" aria-label="Tactical theme">
            {(
              [
                ['Tactics', themes.slice(0, 13)],
                ['Mates', themes.slice(13, 17)],
                ['Endgame & other', themes.slice(17)],
              ] as const
            ).map(([label, items]) => (
              <fieldset className="choice-field" key={label}>
                <legend>{label}</legend>
                <div className="theme-choices">
                  {items.map((theme) => (
                    <button
                      key={theme}
                      title={themeLabel(theme)}
                      aria-label={themeLabel(theme)}
                      aria-pressed={draft.theme === theme}
                      onClick={() => update({ theme })}
                    >
                      <PracticeIcon name={theme} />
                      <span>{shortLabels[theme] ?? themeLabel(theme)}</span>
                    </button>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </>
      )}
      <div className="practice-start">
        <button
          className="primary"
          disabled={reviewing ? !reviewTotal : !!rangeError}
          onClick={() => {
            if (step === 'difficulty' && draft.mode === 'theme') setStep('theme');
            else start();
          }}
        >
          {step === 'theme'
            ? `Start · ${themeLabel(draft.theme)}`
            : reviewing
              ? review?.due || !reviewTotal
                ? 'Start review'
                : 'Review early'
              : draft.mode === 'theme'
                ? 'Next: choose theme'
                : 'Start practice'}
          <Icon name="arrow" />
        </button>
      </div>
    </div>
  );
}
